import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const NewConditionManagement = () => {
  const [conditions, setConditions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    calculationType: 'mixed',
    marginTiers: [{ fromHour: 0, toHour: 500, flatRate: '', marginPercentage: '' }]
  });
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('Payroll Conditions');
    fetchConditions();
  }, [setPageTitle]);

  const fetchConditions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/condition');
      setConditions(response.data);
    } catch (error) {
      toast.error('Error fetching conditions');
      setConditions([]);
    } finally {
      setLoading(false);
    }
  };

  const addTier = () => {
    const lastTier = formData.marginTiers[formData.marginTiers.length - 1];
    const newFromHour = lastTier ? lastTier.toHour + 1 : 0;
    setFormData({
      ...formData,
      marginTiers: [...formData.marginTiers, { fromHour: newFromHour, toHour: '', flatRate: '', marginPercentage: '' }]
    });
  };

  const removeTier = (index) => {
    const newTiers = formData.marginTiers.filter((_, i) => i !== index);
    setFormData({ ...formData, marginTiers: newTiers });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.marginTiers];
    newTiers[index][field] = value;
    setFormData({ ...formData, marginTiers: newTiers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Clean up the data
      const cleanedData = {
        ...formData,
        marginTiers: formData.marginTiers.map(tier => ({
          fromHour: parseInt(tier.fromHour),
          toHour: parseInt(tier.toHour),
          flatRate: tier.flatRate ? parseFloat(tier.flatRate) : undefined,
          marginPercentage: tier.marginPercentage ? parseFloat(tier.marginPercentage) : undefined
        }))
      };

      if (editingCondition) {
        await axios.put(`/api/condition/${editingCondition._id}`, cleanedData);
        toast.success('Condition updated successfully');
      } else {
        await axios.post('/api/condition', cleanedData);
        toast.success('Condition created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchConditions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving condition');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedConditions.map(id => axios.delete(`/api/condition/${id}`)));
      toast.success(`${selectedConditions.length} conditions deleted successfully`);
      setSelectedConditions([]);
      setShowDeleteModal(false);
      fetchConditions();
    } catch (error) {
      toast.error('Error deleting conditions');
    }
  };

  const resetForm = () => {
    setEditingCondition(null);
    setFormData({
      name: '',
      description: '',
      calculationType: 'mixed',
      marginTiers: [{ fromHour: 0, toHour: 500, flatRate: '', marginPercentage: '' }]
    });
  };

  const handleEdit = (condition) => {
    setEditingCondition(condition);
    setFormData({
      name: condition.name,
      description: condition.description || '',
      calculationType: condition.calculationType,
      marginTiers: condition.marginTiers.map(tier => ({
        fromHour: tier.fromHour,
        toHour: tier.toHour,
        flatRate: tier.flatRate || '',
        marginPercentage: tier.marginPercentage || ''
      }))
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this condition?')) {
      try {
        await axios.delete(`/api/condition/${id}`);
        toast.success('Condition deleted successfully');
        fetchConditions();
      } catch (error) {
        toast.error('Error deleting condition');
      }
    }
  };

  if (!hasPermission('condition', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="alert alert-info">
            <strong>How Payroll Conditions Work:</strong><br/>
            Conditions are applied based on cumulative hours worked by an employee across all invoices.
            Each tier can have either a flat rate (consultant gets fixed $/hour) or percentage (consultant gets % of their rate).
          </div>
          {selectedConditions.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="badge bg-primary">{selectedConditions.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
        </div>
        {hasPermission('condition', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus me-2"></i>Add Condition
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading conditions...</p>
        </div>
      ) : conditions.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '4rem', opacity: 0.3 }}>⚙️</div>
          <h5 className="text-muted mt-3">No payroll conditions found</h5>
          <p className="text-muted">Create payroll conditions to define how employee payments are calculated.</p>
          {hasPermission('condition', 'create') && (
            <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Create First Condition
            </button>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedConditions(conditions.map(c => c._id));
                      } else {
                        setSelectedConditions([]);
                      }
                    }}
                    checked={selectedConditions.length === conditions.length && conditions.length > 0}
                  />
                </th>
                <th>Name</th>
              <th>Description</th>
              <th>Tiers</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {conditions.map(condition => (
              <tr key={condition._id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedConditions.includes(condition._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedConditions([...selectedConditions, condition._id]);
                      } else {
                        setSelectedConditions(selectedConditions.filter(id => id !== condition._id));
                      }
                    }}
                  />
                </td>
                <td>{condition.name}</td>
                <td>{condition.description}</td>
                <td>
                  {condition.marginTiers?.map((tier, index) => (
                    <div key={index} className="small">
                      {tier.fromHour}-{tier.toHour} hrs: {tier.flatRate ? `$${tier.flatRate}/hr` : `${tier.marginPercentage}%`}
                    </div>
                  ))}
                </td>
                <td>
                  <span className={`badge ${condition.isActive ? 'bg-success' : 'bg-danger'}`}>
                    {condition.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {hasPermission('condition', 'update') && (
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(condition)}>
                      <i className="fas fa-edit"></i>
                    </button>
                  )}
                  {hasPermission('condition', 'delete') && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(condition._id)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingCondition ? 'Edit Condition' : 'Add Condition'}</h5>
                <button type="button" className="btn-close" onClick={() => { setShowModal(false); resetForm(); }}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows="2"
                    />
                  </div>
                  
                  <h6>Payment Tiers</h6>
                  {formData.marginTiers.map((tier, index) => (
                    <div key={index} className="card mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="card-title mb-0">Tier {index + 1}</h6>
                          {formData.marginTiers.length > 1 && (
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTier(index)}>
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-md-3">
                            <label className="form-label">From Hour</label>
                            <input
                              type="number"
                              className="form-control"
                              value={tier.fromHour}
                              onChange={(e) => updateTier(index, 'fromHour', e.target.value)}
                              required
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">To Hour</label>
                            <input
                              type="number"
                              className="form-control"
                              value={tier.toHour}
                              onChange={(e) => updateTier(index, 'toHour', e.target.value)}
                              required
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Flat Rate ($/hr)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={tier.flatRate}
                              onChange={(e) => updateTier(index, 'flatRate', e.target.value)}
                              placeholder="Leave empty for percentage"
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Percentage (%)</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={tier.marginPercentage}
                              onChange={(e) => updateTier(index, 'marginPercentage', e.target.value)}
                              placeholder="Leave empty for flat rate"
                            />
                          </div>
                        </div>
                        <small className="text-muted">
                          Use either flat rate OR percentage, not both. Flat rate = consultant gets fixed $/hour. Percentage = consultant gets % of their rate.
                        </small>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline-primary" onClick={addTier}>
                    Add Tier
                  </button>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingCondition ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingCondition ? 'Update' : 'Create'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedConditions.length} condition(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedConditions.length} Condition(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewConditionManagement;